import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";

/**
 * GA4 integration — RUNTIME configured.
 *
 * We fetch the Measurement ID from `GET /api/config` at app boot instead of
 * relying on `process.env.REACT_APP_GA4_ID`. CRA's build-time substitution
 * proved unreliable on our Cloud Build pipeline (an empty OS-level env var
 * shadowed the .env value, so the ID silently dropped from the bundle).
 * Backend runtime env vars DO reach the pod reliably, so this is the robust
 * path.
 *
 * Once the ID arrives:
 *   1. We inject `gtag.js` as an async <script>
 *   2. Bootstrap `dataLayer` and `gtag()` (matches Google's standard snippet)
 *   3. Call `gtag("config", GA_ID, { send_page_view: false })` — we fire
 *      page_view ourselves on every React Router location change so SPA
 *      navigation shows up as separate pageviews.
 *
 * Also exports `trackEvent(name, params)` for custom events.
 */

let gaId = null;
let gtagLoaded = false;
let configFetchPromise = null;

async function ensureConfig() {
  if (gaId !== null) return gaId; // already resolved (string or "")
  if (configFetchPromise) return configFetchPromise;
  configFetchPromise = (async () => {
    try {
      const { data } = await api.get("/config");
      gaId = (data?.ga4_id || "").trim();
    } catch (e) {
      gaId = "";
    }
    return gaId;
  })();
  return configFetchPromise;
}

function loadGtag() {
  if (gtagLoaded || !gaId || typeof window === "undefined") return;
  gtagLoaded = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  // Manual page_view below on every route change.
  gtag("config", gaId, { send_page_view: false });

  // Fire the initial page_view once gtag is ready — the SPA may have
  // already landed on a non-root route by the time the config fetch resolves.
  gtag("event", "page_view", {
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function useGA4PageTracking() {
  const location = useLocation();

  useEffect(() => {
    (async () => {
      await ensureConfig();
      loadGtag();
    })();
  }, []);

  useEffect(() => {
    if (!gaId || typeof window === "undefined" || !window.gtag) return;
    const path = location.pathname + location.search;
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
}

/**
 * Fire a custom GA4 event. No-op if GA isn't configured so dev/preview stays clean.
 *
 * Example:
 *   trackEvent("favorite_add", { server_id: "WD-123", game_id: "wardogs" });
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}
